import { log, error } from '@/services/Log'
import type { BCCartType } from '@/types/BigCommerce/BCCart'
import type { StandardOrder } from '@/types/ShopFront/CheckoutStandards'
import type { BCCheckoutType } from '@/types/BigCommerce/BCCheckout'

import { assertBCCart, assertBCCarts } from '@/types/BigCommerce/BCCart'
import { assertBCCheckout } from '@/types/BigCommerce/BCCheckout'
import allPromisesWithRetries from '@/helpers/allPromisesWithRetries'
import Axios from 'axios'

//---------------------------------------------------------------

type OrderRecreator = (input: { order: StandardOrder }) => Promise<{
  success: true,
  newOrder: StandardOrder
} | {
  success: false,
  newOrder: null,
}>
export const deleteCart = async (cartId: string) => {
  await Axios.delete(`/api/storefront/carts/${cartId}`)
}

export const addSingleLineItemToCart = (
  cartId: string,
) => async (lineItem: { productId: number, quantity: number, variantId: number }) => {
  try {
    const response = await Axios.post<BCCartType>(`/api/storefront/carts/${cartId}/items?include=lineItems.physicalItems.options`, {
      lineItems: [
        {
          productId: lineItem.productId,
          quantity: lineItem.quantity,
          variantId: lineItem.variantId,
        },
      ],
    })
    const cart = response.data
    assertBCCarts([cart])
    return cart
  } catch (err) {
    return null
  }
}
export const createCartWithSingleLineItem = async (
  lineItem: { productId: string | number, quantity: number, variantId: number },
) => {
  try {
    log('Creating new cart with', {
      body: {
        lineItems: [{
          productId: lineItem.productId,
          quantity: lineItem.quantity,
          variantId: lineItem.variantId,
        }],
      },
    })
    const response = await Axios.post<BCCartType>('/api/storefront/carts?include=lineItems.physicalItems.options', {
      lineItems: [lineItem],
    })
    const cart = response.data
    assertBCCarts([cart])
    return cart.id
  } catch (err) {
    error('Failed to create cart with line Item')
    return null
  }
}

export const attemptToRecreateCart = async (
  originalLineItems: Array<{ productId: string | number, quantity: number, variantId: number }>,
) => {
  let cartId: null | string = null
  try {
    cartId = await createCartWithSingleLineItem(originalLineItems[0])
  } catch {
    error('attemptToRecreateCart we failed to add the first item to the cart')
  }
  const remainingLineItems = originalLineItems.slice(1)
  if (!cartId && originalLineItems.length > 1) { // the first item was not added, try the rest
    await attemptToRecreateCart(remainingLineItems)
  } else if (cartId) { // we have a cart and we can add the rest of the items in parallel
    try {
      await Promise.all(
        remainingLineItems.map(addSingleLineItemToCart(cartId)),
      )
    } catch {
      error('attemptToRecreateCart some of the items failed to be readded to the cart')
    }
  } else {
    error('attemptToRecreateCart we failed to add any items to the cart')
  }
}

export const recreateOrder: OrderRecreator = async ({ order }) => {
  log('recoverAvailableShippingOptions START', { order })
  const [
    { standardizeOrderFromResponse },
    { standardizeAddressForBigC },
  ] = await allPromisesWithRetries(() => [
    import('@/services/Standardizers/standardizeOrderFromResponse'),
    import('@/services/Standardizers/standardizeAddressForBigC'),
  ])
  try {
    const originalProductsDistribution = (
      order.shipments.map(({ lineItems, shippingAddress }) => ({
        lineItems: lineItems.map(({
          productId, variantId, quantity,
        }) => ({ productId, variantId, quantity })),
        shippingAddress,
      }))
    )
    const originalLineItems = order.cart.lineItems.map(({
      productId, quantity, variantId,
    }) => ({ productId, quantity, variantId }))
    log({ originalProductsDistribution, originalLineItems })
    // Delete current cart
    log(' Deleting cart', { cart: order?.cart })
    await deleteCart(order?.cart.id)
    // Add Items to new cart
    log('Creating a new cart with previous items', { lineItems: originalLineItems })
    const newCart = (await Axios.post<BCCartType>('/api/storefront/carts', { lineItems: originalLineItems })).data
    assertBCCart(newCart)
    // TODO we cannot recreate custom items since the BIGC return type does not contain:
    // TODO productId & variantId
    const newLineItems = [...newCart.lineItems.physicalItems]
    // Create consignments
    const newConsignments = (
      originalProductsDistribution
        .map(({ shippingAddress, lineItems }) => ({
          shippingAddress: standardizeAddressForBigC(shippingAddress),
          lineItems: lineItems.map(({
            productId, variantId, quantity,
          }) => ({
            itemId: String(newLineItems.find((p) => (
              p.productId === productId && p.variantId === variantId
            ))?.id),
            quantity,
          })),
        }))
    )
    log('Recreating consignments', { newCart, newLineItems, newConsignments })
    const bigcResponse = (await Axios.post<BCCheckoutType>(`/api/storefront/checkouts/${newCart?.id}/consignments?include=consignments.availableShippingOptions`, newConsignments))?.data
    assertBCCheckout(bigcResponse)
    const newOrder = await standardizeOrderFromResponse({ bigcResponse, order })
    log('New standardized order', { newOrder })
    return { newOrder, success: true }
  } catch (err) {
    error('recoverAvailableShippingOptions ERROR', err)
  }
  return { success: false, newOrder: null }
}

export default recreateOrder
